import { forwardRef } from 'react';

import { GosFormData, GosFormEye, GosFormSignature } from '../../@types/pcse';
import { formatLabel } from '../../utils/formatLabel';

/**
 * The official NHS GOS paper form, rendered for printing.
 *
 * Plain semantic HTML with a scoped stylesheet rather than MUI components: this
 * has to survive `window.print()` on A4 in every browser, and MUI's emotion
 * styles, elevation shadows and theme colours all print badly (or not at all,
 * once "background graphics" is off — which it is by default).
 *
 * Every figure comes from the server's snapshot of the claim, never from a
 * fresh calculation: the printed form is a record of what was claimed.
 */

const money = (n?: number) => `£${(Number(n) || 0).toFixed(2)}`;

const date = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-GB') : '—';

const dateTime = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('en-GB') : '—';

function describeEye(eye?: GosFormEye): string {
  if (!eye || (eye.sphere == null && eye.cylinder == null)) return '—';
  const parts = [(eye.sphere ?? 0).toFixed(2)];
  if (eye.cylinder != null) {
    parts.push(`/ ${eye.cylinder.toFixed(2)}`);
    if (eye.axis != null) parts.push(`x ${eye.axis}`);
  }
  if (eye.add != null && eye.add !== 0) parts.push(`add ${eye.add.toFixed(2)}`);
  if (eye.prism != null && eye.prism !== 0) {
    parts.push(
      `prism ${eye.prism.toFixed(2)}${eye.base ? ` ${eye.base}` : ''}`,
    );
  }
  return parts.join(' ');
}

/** Human title for a claim type, as printed at the head of the form. */
const FORM_TITLES: Record<string, string> = {
  GOS1: 'GOS 1 — NHS sight test',
  GOS3: 'GOS 3 — NHS optical voucher',
  GOS4: 'GOS 4 — Repair or replacement',
  GOS5: 'GOS 5 — Private sight test reimbursement',
  GOS6: 'GOS 6 — Domiciliary sight test',
  GOS6_PVN: 'GOS 6 PVN — Domiciliary pre-visit notification',
};

const SIGNATORY_LABELS: Record<string, string> = {
  patient: 'Patient',
  guardian: 'Parent / guardian',
  optometrist: 'Performer (optometrist)',
  performer: 'Performer (optometrist)',
  contractor: 'Contractor',
  supplier: 'Supplier / dispenser',
};

/**
 * Print styles. `printMode` is only set on the standalone print window, so the
 * on-screen preview keeps a page-like frame while the printed sheet has none.
 */
const STYLES = `
.gos-form { font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff;
  font-size: 11pt; line-height: 1.35; max-width: 190mm; margin: 0 auto; padding: 8mm; }
.gos-form * { box-sizing: border-box; }
.gos-form h1 { font-size: 15pt; margin: 0 0 2mm; }
.gos-form h2 { font-size: 11pt; margin: 0 0 2mm; text-transform: uppercase;
  letter-spacing: .04em; border-bottom: 1px solid #000; padding-bottom: 1mm; }
.gos-head { display: flex; justify-content: space-between; align-items: flex-start;
  border-bottom: 2px solid #000; padding-bottom: 3mm; margin-bottom: 4mm; }
.gos-head .meta { text-align: right; font-size: 9pt; }
.gos-section { margin-bottom: 5mm; page-break-inside: avoid; }
.gos-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1mm 6mm; }
.gos-grid.cols-3 { grid-template-columns: repeat(3, 1fr); }
.gos-field { display: flex; gap: 2mm; padding: 1mm 0; border-bottom: 1px dotted #999; }
.gos-field .label { font-weight: bold; min-width: 42mm; }
.gos-field .value { flex: 1; }
.gos-table { width: 100%; border-collapse: collapse; font-size: 10pt; }
.gos-table th, .gos-table td { border: 1px solid #000; padding: 1.5mm 2mm; text-align: left; }
.gos-table th { background: #eee; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.gos-table td.num, .gos-table th.num { text-align: right; }
.gos-ticks { display: flex; flex-wrap: wrap; gap: 2mm 6mm; }
.gos-tick { display: flex; align-items: center; gap: 1.5mm; }
.gos-tick .box { width: 4mm; height: 4mm; border: 1px solid #000; display: inline-block;
  text-align: center; line-height: 3.6mm; font-weight: bold; }
.gos-signatures { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4mm; }
.gos-signature { border: 1px solid #000; padding: 2mm; page-break-inside: avoid; }
.gos-signature .role { font-weight: bold; font-size: 9pt; text-transform: uppercase; }
.gos-signature img { display: block; width: 100%; max-height: 22mm; object-fit: contain;
  margin: 1mm 0; background: #fff; }
.gos-signature .when { font-size: 8.5pt; color: #333; }
.gos-note { font-size: 8.5pt; color: #333; margin-top: 2mm; }
.gos-foot { margin-top: 6mm; padding-top: 2mm; border-top: 1px solid #000;
  font-size: 8.5pt; display: flex; justify-content: space-between; }
.gos-empty { color: #666; font-style: italic; }
@media print {
  @page { size: A4 portrait; margin: 10mm; }
  .gos-form { padding: 0; max-width: none; }
}
`;

export const GOS_PRINT_STYLES = STYLES;

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="gos-field">
      <span className="label">{label}</span>
      <span className="value">
        {value === undefined || value === null || value === '' ? (
          <span className="gos-empty">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

function Signature({ signature }: { signature: GosFormSignature }) {
  return (
    <div className="gos-signature">
      <div className="role">
        {SIGNATORY_LABELS[signature.signatory?.toLowerCase()] ??
          signature.signatory}
      </div>
      {signature.image ? (
        <img src={signature.image} alt={`${signature.signatory} signature`} />
      ) : (
        <div className="gos-empty">No signature captured</div>
      )}
      <div>{signature.name || <span className="gos-empty">—</span>}</div>
      <div className="when">Signed {dateTime(signature.signedAt)}</div>
    </div>
  );
}

interface GosClaimPrintoutProps {
  form: GosFormData;
}

const GosClaimPrintout = forwardRef<HTMLDivElement, GosClaimPrintoutProps>(
  ({ form }, ref) => {
    const { claim, contractor, patient, eligibility, prescription, voucher } =
      form;

    return (
      <div className="gos-form" ref={ref}>
        <style>{STYLES}</style>

        <div className="gos-head">
          <div>
            <h1>{FORM_TITLES[claim.type] ?? claim.type}</h1>
            <div>NHS Ophthalmic Services claim</div>
          </div>
          <div className="meta">
            <div>
              <strong>Claim</strong> {claim.id}
            </div>
            {claim.reference && (
              <div>
                <strong>PCSE ref</strong> {claim.reference}
              </div>
            )}
            <div>
              <strong>Status</strong> {formatLabel(claim.status)}
            </div>
            <div>
              <strong>Submitted</strong> {dateTime(claim.submittedAt)}
            </div>
          </div>
        </div>

        {/* ── Part 1: patient ─────────────────────────────────────── */}
        <div className="gos-section">
          <h2>Part 1 — Patient details</h2>
          <div className="gos-grid">
            <Field label="Surname" value={patient.lastName} />
            <Field label="First names" value={patient.firstName} />
            <Field label="Date of birth" value={date(patient.dateOfBirth)} />
            <Field label="Sex" value={patient.sex} />
            <Field label="Address" value={patient.addressLines.join(', ')} />
            <Field label="Postcode" value={patient.postcode} />
            <Field label="NHS number" value={patient.nhsNumber} />
            <Field label="NI number" value={patient.niNumber} />
          </div>
        </div>

        {/* ── Part 2: eligibility ─────────────────────────────────── */}
        <div className="gos-section">
          <h2>Part 2 — Patient declaration of entitlement</h2>
          <div className="gos-ticks">
            {[
              'Aged under 16',
              'Aged 60 or over',
              'Aged 16, 17 or 18 and in full-time education',
              'In receipt of a qualifying benefit / HC2',
              'Diagnosed with diabetes or glaucoma',
              'Aged 40 or over with a family history of glaucoma',
            ].map((label) => (
              <span className="gos-tick" key={label}>
                <span className="box">
                  {eligibility.reasons.includes(label) ? '✓' : ''}
                </span>
                {label}
              </span>
            ))}
          </div>
          <div className="gos-note">
            Evidence of entitlement seen and verified:{' '}
            <strong>{eligibility.evidenceSeen ? 'Yes' : 'No'}</strong>. Patient
            declaration confirmed:{' '}
            <strong>{form.declaration.patientDeclared ? 'Yes' : 'No'}</strong>.
          </div>
        </div>

        {/* ── Prescription ────────────────────────────────────────── */}
        {prescription && (
          <div className="gos-section">
            <h2>Prescription</h2>
            <table className="gos-table">
              <thead>
                <tr>
                  <th>Eye</th>
                  <th>Sphere / Cylinder / Axis / Add</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Right</td>
                  <td>{describeEye(prescription.right)}</td>
                </tr>
                <tr>
                  <td>Left</td>
                  <td>{describeEye(prescription.left)}</td>
                </tr>
              </tbody>
            </table>
            <div className="gos-grid" style={{ marginTop: '2mm' }}>
              <Field label="Date of test" value={date(prescription.testDate)} />
              <Field
                label="Performer list no."
                value={contractor.performerListNumber}
              />
            </div>
          </div>
        )}

        {/* ── Part 3: voucher ─────────────────────────────────────── */}
        {voucher && (
          <div className="gos-section">
            <h2>Part 3 — Optical voucher</h2>
            <div className="gos-grid cols-3">
              <Field label="Voucher band" value={voucher.band} />
              <Field label="Appliance" value={voucher.appliance} />
              <Field label="Reason supplied" value={voucher.reason} />
              <Field label="Voucher code" value={voucher.code} />
              <Field
                label="Supplements"
                value={voucher.supplements.join(', ')}
              />
              <Field label="Valued on" value={date(voucher.valuedOn)} />
            </div>

            {voucher.pairs.length > 0 && (
              <table className="gos-table" style={{ marginTop: '2mm' }}>
                <thead>
                  <tr>
                    <th>Pair</th>
                    <th>Band</th>
                    <th className="num">Voucher value</th>
                    <th className="num">Charged</th>
                    <th className="num">NHS pays</th>
                    <th className="num">Patient pays</th>
                  </tr>
                </thead>
                <tbody>
                  {voucher.pairs.map((pair, i) => (
                    <tr key={i}>
                      <td>{i === 0 ? 'First' : 'Second'}</td>
                      <td>{pair.type}</td>
                      <td className="num">{money(pair.value)}</td>
                      <td className="num">{money(pair.chargedAmount)}</td>
                      <td className="num">{money(pair.redeemedValue)}</td>
                      <td className="num">{money(pair.patientContribution)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <table className="gos-table" style={{ marginTop: '2mm' }}>
              <tbody>
                <tr>
                  <th>Total charged to patient</th>
                  <td className="num">{money(voucher.chargedAmount)}</td>
                </tr>
                <tr>
                  <th>Claimed from NHS</th>
                  <td className="num">{money(voucher.value)}</td>
                </tr>
                <tr>
                  <th>Paid by patient</th>
                  <td className="num">{money(voucher.patientContribution)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ── GOS4 repair ─────────────────────────────────────────── */}
        {form.repair && (
          <div className="gos-section">
            <h2>Repair / replacement</h2>
            <div className="gos-grid cols-3">
              <Field label="Action" value={form.repair.action} />
              <Field label="Component" value={form.repair.component} />
              <Field label="Reason" value={form.repair.reason} />
            </div>
          </div>
        )}

        {/* ── Signatures ──────────────────────────────────────────── */}
        <div className="gos-section">
          <h2>Declarations &amp; signatures</h2>
          <div className="gos-signatures">
            {form.declaration.signatures.length === 0 && (
              <div className="gos-empty">
                No signatures are held against this claim.
              </div>
            )}
            {form.declaration.signatures.map((signature, i) => (
              <Signature
                key={`${signature.signatory}-${i}`}
                signature={signature}
              />
            ))}
          </div>

          <div className="gos-note">
            Contractor sign-off:{' '}
            <strong>
              {form.declaration.contractorSignedOff ? 'Yes' : 'No'}
            </strong>
            {form.declaration.patientSignatureSource === 'dispense' && (
              <>
                {' '}
                · Patient signature reused from the signature captured at
                dispense
                {claim.dispenseId ? ` #${claim.dispenseId}` : ''} — the patient
                was not asked to sign a second time for the same supply.
              </>
            )}
            {form.staffSignature && (
              <>
                {' '}
                · Submitted by {form.staffSignature.name} on{' '}
                {dateTime(form.staffSignature.signedAt)}.
              </>
            )}
          </div>
        </div>

        <div className="gos-foot">
          <span>
            Contractor ODS: {contractor.odsCode ?? '—'}
            {claim.environment ? ` · ${claim.environment}` : ''}
          </span>
          <span>Printed {new Date().toLocaleString('en-GB')}</span>
        </div>
      </div>
    );
  },
);

GosClaimPrintout.displayName = 'GosClaimPrintout';

export default GosClaimPrintout;
